import { postContact } from 'api/goodtrust/contact'
import { useUserMe } from 'api/goodtrust/user'
import { Button } from 'components/button/Button'
import { Checkbox } from 'components/checkbox/Checkbox'
import {
  SForm,
  SModalMessageContainer,
  SModalMessageLogoContainer,
  SQuestionMarkRow,
  STextArea,
} from './ContactCreateForm.styled'
import { useContactInfoModal } from 'components/contact/contactInfoModal/ContactInfoModal'
import { Fieldset } from 'components/fieldset/Fieldset'
import { FormButton, FormFieldset } from 'components/FormButton'
import { Icon } from 'components/icon'
import { Select } from 'components/select/Select'
import { handleAndToastError } from 'components/Toast'
import { Text } from 'components/Typography'
import { WizardSubtitle } from 'components/wizard/WizardTypography'
import { useTranslation } from 'next-i18next'
import React, { useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { fireEvent } from 'utils/analytics'
import { LogoOfTheDay } from 'utils/cobranding/LogoOfTheDay'
import { colors } from 'utils/colors'
import { ApiError } from 'utils/error'
import { unwrapResponse } from 'utils/fetcher'
import { useRunOnceReady, useWindowSize } from 'utils/hooks'
import { useFieldValidation } from 'utils/i18n/useFieldValidation'
import { ApiType } from 'utils/types'
import { USE_RELATIONSHIP_OPTIONS } from 'utils/constants'

interface FormFields {
  email: string
  relationship?: ApiType['TrustedContactResponse']['relationship'] | ''
  show_note?: boolean
  note?: string
  isLegacy?: boolean
}

// TODO: [Localization] This component is handled at gt-4404
export const ContactCreateForm = (props: {
  gaType?: 'share'
  email?: string
  onSuccess?: (data: ApiType['TrustedContactResponse']) => void
  isLegacy?: boolean
  buttonLabel?: string
}) => {
  const { t } = useTranslation('trusted_contact')
  const validation = useFieldValidation()
  useRunOnceReady(true, () => {
    fireEvent('tc_add_modal_opened', { type: props.gaType || 'other' })
  })

  const [infoModal, openInfoModal] = useContactInfoModal(true)

  const { isLegacy, onSuccess } = props
  const { isDesktop } = useWindowSize()
  const { data: user } = useUserMe()
  const form = useForm<FormFields>({
    defaultValues: {
      email: props.email || '',
      relationship: '',
      show_note: false,
      note: '',
      isLegacy: !!isLegacy,
    },
  })

  const onSubmit = async (data: FormFields) => {
    try {
      const res = await postContact({
        email: data.email,
        note: data.note || undefined,
        relationship: data.relationship || undefined,
        type: isLegacy || data.isLegacy ? 'LEGACY_CONTACT' : 'STANDARD',
      }).then(unwrapResponse)
      if (res.json?.uuid == null) throw new ApiError(res)
      await onSuccess?.(res.json)
    } catch (err) {
      handleAndToastError(err, 'failed_to_add_contact')
    }
  }

  const show_note = form.watch('show_note')

  useEffect(() => {
    if (show_note) {
      form.setFocus('note')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show_note])

  const relationshipOptions = USE_RELATIONSHIP_OPTIONS()
  return (
    <SForm noValidate onSubmit={form.handleSubmit(onSubmit)}>
      {infoModal}
      <FormFieldset form={form}>
        <Fieldset>
          <input
            {...form.register('email', {
              ...validation.required(),
              ...validation.email(),
            })}
            type="email"
            placeholder="&nbsp;"
          />
          <label>{t('trusted_contact.directive.contact_create_form_email_label')}</label>
        </Fieldset>
      </FormFieldset>
      <Controller
        name="relationship"
        control={form.control}
        render={({ field }) => (
          <FormFieldset name={field.name} form={form}>
            <Select
              {...field}
              label={
                isDesktop
                  ? isLegacy
                    ? t('trusted_contact.directive.relationship_legacy_label')
                    : t('trusted_contact.directive.relationship_trusted_label')
                  : t('trusted_contact.directive.relationship_label_mobile')
              }
              items={relationshipOptions}
            />
          </FormFieldset>
        )}
      />

      {!isLegacy && (
        <FormFieldset form={form}>
          <SQuestionMarkRow>
            <Checkbox {...form.register('isLegacy')}>
              <Text>{t('trusted_contact.directive.contact_create_form_also_legacy')}</Text>
            </Checkbox>
            <Icon name="Question" color={colors.gray[500]} onClick={openInfoModal} />
          </SQuestionMarkRow>
        </FormFieldset>
      )}

      <FormFieldset form={form}>
        <Checkbox {...form.register('show_note')}>
          <Text>{t('trusted_contact.directive.contact_create_form_add_personal_message')}</Text>
        </Checkbox>
      </FormFieldset>

      {form.watch('show_note') && (
        <>
          <WizardSubtitle>
            {t('trusted_contact.directive.contact_create_form_show_note_subtitle')}
          </WizardSubtitle>
          <SModalMessageContainer>
            <SModalMessageLogoContainer>
              <LogoOfTheDay />
            </SModalMessageLogoContainer>
            <Text>
              {t('trusted_contact.directive.contact_create_form_show_note_note', {
                who: user?.json?.first_name || user?.json?.email,
              })}
            </Text>
            <STextArea {...form.register('note')} css={{ resize: 'vertical' }} />
          </SModalMessageContainer>
        </>
      )}
      <FormButton form={form}>
        <Button size="large" type="button" onClick={form.handleSubmit(onSubmit)}>
          {props.buttonLabel ||
            t('trusted_contact.directive.contact_create_form_send_invite_button')}
        </Button>
      </FormButton>
    </SForm>
  )
}
